import debounce from 'lodash/debounce'
import { Utility } from '../utility/utility_controller'
import projects_overlays from '../projects_overlays/projects_overlays'
import animation_lifecycle_controller from '../animations/lifecycle/animation_lifecycle_controller'
import { getScrollContainerForElement } from '../scroll_animations/viewport_progress'
import { isRevealRearmLocked, setElementBelow, showElementInView } from './reveal_state'
import { isRevealAnimationDisabled } from './element_animate_in_on_scroll_helpers'

// Resolve the IO root + its visible rect for a target inside a page
// overlay. For centered modal-style overlays (with space-around / smaller
// width) the IO's default window-viewport root makes trigger lines and
// below-viewport checks misfire — we want them measured against the
// overlay's actual visible area.
const getIORootForTarget = (target) => {
	const pageOverlay = target && typeof target.closest === 'function' ? target.closest('.lay-overlay') : null
	if (pageOverlay) {
		return {
			root: pageOverlay,
			rect: pageOverlay.getBoundingClientRect(),
		}
	}
	const container = getScrollContainerForElement(target)
	if (!container || container === window) {
		return {
			root: null,
			rect: {
				top: 0,
				left: 0,
				right: window.innerWidth,
				bottom: window.innerHeight,
				width: window.innerWidth,
				height: window.innerHeight,
			},
		}
	}
	return {
		root: container,
		rect: container.getBoundingClientRect(),
	}
}

// separated this out. otherwise we get wrong timeout values in firefox, like 5 seconds :D, because i used to have elements all combined here: on normal page, page overlays and project overlays
let items
const isOpacityAnimationEnabled = () => {
	const val = layData.misc_options_element_transition_animate_opacity
	return val !== '' && val !== '0' && val !== 0
}
const shouldApplyTransitionDelay = () => isOpacityAnimationEnabled()
const NAV_TRANSITION_TARGET_CLASS = 'lay-nav-transition-in-target'
const getScrollTransitionOffset = () => {
	const rawOffset = Utility.isPhoneSize ? layData.misc_options_scroll_transition_offset_mobile : layData.misc_options_scroll_transition_offset_desktop
	const offset = Number.parseInt(rawOffset, 10)
	return Number.isFinite(offset) ? offset : 0
}
const getActualRootBottomForTarget = (target) => getIORootForTarget(target).rect.bottom
const getObserverOptions = (ioRoot) => ({
	threshold: 0,
	root: ioRoot,
	rootMargin: '0px 0px ' + getScrollTransitionOffset() + 'px 0px',
})
const isNavigationTransitionTarget = (element) => {
	return !!(
		element.classList.contains(NAV_TRANSITION_TARGET_CLASS) ||
		(element.closest && element.closest('.' + NAV_TRANSITION_TARGET_CLASS)) ||
		(element.querySelector && element.querySelector('.' + NAV_TRANSITION_TARGET_CLASS))
	)
}
const hasActiveNavigationTransition = (element) => {
	return !!(
		animation_lifecycle_controller.isNavigationAnimating() ||
		(element &&
		(
			(element.closest && element.closest('.disable-css-transitions')) ||
			(element.querySelector && element.querySelector('.disable-css-transitions')) ||
			isNavigationTransitionTarget(element)
		))
	)
}
const showNavigationTransitionTargetIfVisibleAtRest = (element, rootBottom) => {
	if (!isNavigationTransitionTarget(element)) {
		return false
	}
	const rect = element.getBoundingClientRect()
	if (rect.width === 0 && rect.height === 0) {
		return false
	}
	if (rect.top < rootBottom && rect.bottom > 0) {
		showElementInView(element)
		return true
	}
	return false
}
const rearmElementBelowViewport = (item) => {
	if (item.classList.contains('below') || isRevealRearmLocked(item) || isRevealAnimationDisabled(item)) {
		return
	}
	const rect = item.getBoundingClientRect()
	if (rect.top > getActualRootBottomForTarget(item)) {
		setElementBelow(item)
	}
}

const revealBelowElementIfInViewport = (item) => {
	if (!doAnimation || !item.classList.contains('below') || isRevealAnimationDisabled(item)) {
		return
	}
	if (hasActiveNavigationTransition(item)) {
		return
	}
	if (!shouldAnimateElement(item)) {
		return
	}

	const rect = item.getBoundingClientRect()
	if (rect.width === 0 && rect.height === 0) {
		return
	}
	const rootRect = getIORootForTarget(item).rect
	if (rect.bottom <= rootRect.top || rect.top >= rootRect.bottom + getScrollTransitionOffset()) {
		return
	}

	if (shouldApplyTransitionDelay()) {
		item.style.transitionDelay = getTransitionDelayAlt(rect) + 'ms'
	} else {
		item.style.transitionDelay = ''
	}
	showElementInView(item)
}

const SCROLLSLIDER_CONTENT_CLASSNAMES = ['scrollslider-active', 'infinite-scrollslider-active']
const ROW_BACKGROUND_SELECTOR = '.row.has-background > .lay-row-bg-color, .row.has-background > .background-image, .row.has-background > .background-video, .row.has-background > .lay-webgl-slideshow, .row.has-background > .lay-row-canvas-animation:not(.lay-row-canvas-animation-field-zoom), .row.has-background > .lay-row-bg-color-fade, .row > .lay-row-gutter-bg'
const LOAD_MORE_WRAPPER_SELECTORS = ['.lay-thumbnailgrid-load-more-wrap', '.lay-elementgrid-load-more-wrap', '.lay-productsgrid-load-more-wrap', '.lay-news-load-more-wrap']
const getScopedSelectorList = (parent, selectors, suffix = '') => selectors.map((selector) => parent + ' ' + selector + suffix).join(',')
const shouldAnimateRowBackgroundsOnScroll = () => {
	const val = layData.misc_options_scroll_transition_animate_row_backgrounds
	return val == null || val === true || val === '1' || val === 1 || val === 'on'
}
const getScopedRowBackgroundSelector = (parent) => {
	if (!shouldAnimateRowBackgroundsOnScroll()) {
		return ''
	}
	return ROW_BACKGROUND_SELECTOR.split(',').map((selector) => parent + ' ' + selector.trim()).join(',')
}

const isScrollSliderActiveGlobally = () => {
	return window.layscrollslider != undefined && typeof window.layscrollslider.isActiveHere === 'function' && window.layscrollslider.isActiveHere()
}

const isInsideScrollSliderContent = (element) => {
	if (!element) {
		return false
	}

	const layContentParent = element.closest('.lay-content')
	if (!layContentParent) {
		return false
	}

	if (layContentParent.closest('#lay-project-overlay')) {
		return false
	}

	if (SCROLLSLIDER_CONTENT_CLASSNAMES.some((cls) => layContentParent.classList.contains(cls))) {
		return true
	}

	if (isScrollSliderActiveGlobally() && layContentParent.matches && layContentParent.matches('body>.lay-content')) {
		return true
	}

	return false
}

const filterOutScrollSliderElements = (nodeList) => {
	return Array.from(nodeList).filter((el) => !isInsideScrollSliderContent(el))
}

var i = function () {
	if (window.layData.element_transition_on_scroll == 'on' && window.layData.page_overlay_feature == 'on' && 'IntersectionObserver' in window) {
		window.laytheme.on('overlayshown_animation_ended', function ($target) {
			let l = $target.attr('data-link')
			let pageOverlayParent = '.lay-overlay[data-link="' + l + '"]'

			// Exclude grid-type containers (.type-elementgrid, .type-thumbnailgrid, .type-productsgrid, .type-news)
			// as they should never get .below/.in-view classes - only their inner content elements should
			// Also exclude elements inside collapsed rows so opening animation measures row height correctly
			const revealSelector = [
				pageOverlayParent + ' .col.no-parallax.no-offset.no-sticky:not(.type-elementgrid):not(.type-thumbnailgrid):not(.type-productsgrid):not(.type-news):not(.inside-collapsed-row)',
				pageOverlayParent + ' .col.no-parallax.no-offset.type-thumbnailgrid:not(.inside-collapsed-row) .thumb',
				pageOverlayParent + ' .col.no-parallax.no-offset.type-elementgrid:not(.inside-collapsed-row) .element-wrap',
				pageOverlayParent + ' .col.no-parallax.no-offset.type-productsgrid:not(.inside-collapsed-row) li',
				pageOverlayParent + ' .lay-thumbnailgrid-filter-wrap',
				pageOverlayParent + ' .show-news',
				pageOverlayParent + ' .hide-news',
				getScopedSelectorList(pageOverlayParent, LOAD_MORE_WRAPPER_SELECTORS),
				getScopedRowBackgroundSelector(pageOverlayParent)
			].filter(Boolean).join(',')
			items = filterOutScrollSliderElements(document.querySelectorAll(revealSelector)).filter((element) => !isRevealAnimationDisabled(element))

			// All items here belong to the SAME page overlay (queried via
			// pageOverlayParent), so they share a single scroll container.
			// Pick it from any item — used as IO root + as the rect against
			// which initial below-viewport tests are made. Without this,
			// space-around / centered modal overlays would misclassify
			// elements as below the WINDOW viewport even when they sit
			// inside the overlay's visible area (or vice versa).
			const { root: ioRoot, rect: rootRect } = items.length > 0
				? getIORootForTarget(items[0])
				: { root: null, rect: { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight, width: window.innerWidth, height: window.innerHeight } }

			// add below classes to below elements
			// if i dont use a timeout this doesnt work well :), because masonry grids might need some time to be initialized
			setTimeout(function () {
				items.forEach((el) => {
					if (isRevealAnimationDisabled(el)) {
						return
					}

					// Skip while the page-overlay open animation is currently
					// transforming this element — same race condition as on the
					// main page; see element_animate_in_on_scroll.js.
					if (showNavigationTransitionTargetIfVisibleAtRest(el, getActualRootBottomForTarget(el))) {
						return
					}
					if (hasActiveNavigationTransition(el)) {
						return
					}

					// Check if element is hidden (display: none)
					var rect = el.getBoundingClientRect()
					var isHidden = rect.width === 0 && rect.height === 0
					var top = rect.top

						// Give hidden elements the 'below' class so they animate when shown.
						// In page overlays, do not use the scroll-transition offset for the
							// initial below-state. Partially visible elements near the bottom of
							// the overlay should stay visible, not get opacity:0 via .below.
							if (isHidden || top > rootRect.bottom) {
								setElementBelow(el)
							} else {
						// need this cause otherwise, if we open a overlay, close it, and open it again, some elements might not be shown
						showElementInView(el)
					}
				})
			}, 500)

			// only bind observer once per page overlay:
			if ($target.hasClass('lay-overlay-was-opened-at-least-once')) {
				return
			}

			let options = getObserverOptions(ioRoot)

			setTimeout(function () {
				// just observing here already triggers the intersection observer. so i also need to put it into a timeout because masonry might not be initialized yet
				let observer = new IntersectionObserver(callback, options)
				// observe elements
				const observeWhenTransitionSettled = function (el, attempt = 0) {
					if (hasActiveNavigationTransition(el) && (animation_lifecycle_controller.isNavigationAnimating() || attempt < 20)) {
						setTimeout(function () {
							observeWhenTransitionSettled(el, attempt + 1)
						}, 100)
						return
					}
					if (hasActiveNavigationTransition(el)) {
						observer.observe(el)
						return
					}

					if (!el.classList.contains('below') && !el.classList.contains('in-view') && !isRevealAnimationDisabled(el)) {
						if (showNavigationTransitionTargetIfVisibleAtRest(el, getActualRootBottomForTarget(el))) {
							observer.observe(el)
							return
							}
							var rect = el.getBoundingClientRect()
							var isHidden = rect.width === 0 && rect.height === 0
							if (isHidden || rect.top > rootRect.bottom) {
								el.classList.add('below')
							}
						}

					observer.observe(el)
				}
				items.forEach((el) => {
					observeWhenTransitionSettled(el)
				})
			}, 500)
		})

		// scrolled to bottom
		// if i wouldn't do this, small elements that are very close to the bottom would not ever show up
		window.eventsChannel.on('scroll', function (scrollTop, scrollHeight, scrollContext) {
			if (items) {
				items.forEach(revealBelowElementIfInViewport)
				items.forEach(rearmElementBelowViewport)
			}
			const visibleHeight = scrollContext && scrollContext.source === 'page-overlay' && scrollContext.target
				? scrollContext.target.clientHeight
				: window.innerHeight
			if (visibleHeight + scrollTop >= scrollHeight - 200) {
				if (items) {
					for (let i = 0; i < items.length; i++) {
						const item = items[i]
						if (item.classList.contains('below')) {
							// Check if element should be animated (e.g., thumb with hidden parent should be skipped)
							if (!shouldAnimateElement(item)) {
								continue // Skip animation - parent is hidden
							}

							if (shouldApplyTransitionDelay()) {
								const transitionDelay = getTransitionDelayAlt(item.getBoundingClientRect())
								item.style.transitionDelay = transitionDelay + 'ms'
							} else {
								item.style.transitionDelay = ''
							}

							showElementInView(item)
						}
					}
				}
			}
		})

		bindResize()
	}
}

var bindResize = function () {
	jQuery(window).off('window_width_changed', doResize)
	jQuery(window).on('window_width_changed', doResize)
}

var doResize = debounce(function () {
	if (items) {
	}
}, 2000)

window.laytheme.on('pushstate', function () {
	pauseOnScrollAnimation()
})
window.laytheme.on('popstate', function () {
	pauseOnScrollAnimation()
})
window.laytheme.on('newpageshown', function () {
	setTimeout(function () {
		// Don't resume animation if project overlay is closing
		// This prevents underlying elements from animating when the overlay closes
		if (window.utility_controller.isProjectsOverlayEnabled() && projects_overlays.shouldCloseOverlay()) {
			// Animation will be resumed by the destroyoverlay event
			return
		}
		resumeOnScrollAnimation()
	}, 50)
})

// Resume animations after project overlay is destroyed and scroll snap is cleaned up
// Using destroyoverlay instead of projectoverlayclosed ensures scroll snap is fully destroyed
// before we check element positions (prevents incorrect "below" class with horizontal scroll snap)
window.laytheme.on('destroyoverlay', function () {
	setTimeout(function () {
		resumeOnScrollAnimation()
	}, 50)
})

var doAnimation = true
var resumeOnScrollAnimation = function () {
	doAnimation = true
}
var pauseOnScrollAnimation = function () {
	doAnimation = false
}

/**
 * Check if an element should be animated.
 * For .thumb elements, ensures parent .thumbnail-wrap is visible (not hidden by .hide-loadmore or display: none).
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} - true if element should be animated, false if it should be skipped
 */
var shouldAnimateElement = function (element) {
	if (window.getComputedStyle(element).display === 'none') {
		return false // Skip animation - element is hidden
	}
	// For .thumb elements, check if parent .thumbnail-wrap is visible
	if (element.classList.contains('thumb')) {
		const parentWrap = element.closest('.thumbnail-wrap')
		if (parentWrap) {
			// Check if parent has hide-loadmore class or is display: none
			if (parentWrap.classList.contains('hide-loadmore')) {
				return false // Skip animation - parent is hidden
			}
			// Also check computed style to be safe
			const parentStyle = window.getComputedStyle(parentWrap)
			if (parentStyle.display === 'none') {
				return false // Skip animation - parent is display: none
			}
		}
	}
	return true // Element can be animated
}

let callback = (entries, observer) => {
	entries.forEach((entry) => {
		// Skip while the navigation animation is currently transforming
		// this element — same race condition as on the main page.
		if (hasActiveNavigationTransition(entry.target)) {
			return
		}
		// Each entry describes an intersection change for one observed
		if (doAnimation) {
			// 	'entry.boundingClientRect',
			// 	entry.boundingClientRect,
			// 	'entry.intersectionRatio',
			// 	entry.intersectionRatio,
			// 	'entry.intersectionRect',
			// 	entry.intersectionRect,
			// 	'entry.isIntersecting',
			// 	entry.isIntersecting,
			// 	'entry.rootBounds',
			// 	entry.rootBounds,
			// 	'entry.target',
			// 	entry.target,
			// 	'entry.time',
			// 	entry.time
			// )

			const rootTop = entry.rootBounds ? entry.rootBounds.top : 0
			if (entry.isIntersecting == true && entry.target.classList.contains('below') && entry.boundingClientRect.bottom > rootTop) {
				if (!isRevealAnimationDisabled(entry.target)) {
					// Check if element should be animated (e.g., thumb with hidden parent should be skipped)
					if (!shouldAnimateElement(entry.target)) {
						return // Skip animation - parent is hidden
					}

					// in view
					if (shouldApplyTransitionDelay()) {
						const transitionDelay = getTransitionDelayAlt(entry.boundingClientRect)
						entry.target.style.transitionDelay = transitionDelay + 'ms'
					} else {
						entry.target.style.transitionDelay = ''
					}

					showElementInView(entry.target)
				}
			}
			// Compare against the actual visible overlay/window bottom so
			// replay re-arming waits until the element is truly below view.
			const rootBottom = getActualRootBottomForTarget(entry.target)
			if (entry.isIntersecting == false && entry.boundingClientRect.top > rootBottom && !isRevealRearmLocked(entry.target) && !isRevealAnimationDisabled(entry.target)) {
				// below view
				setElementBelow(entry.target)
			}
		}
	})
}

var multiplyWith = 1

var getTransitionDelayAlt = function (rect) {
	if (!isOpacityAnimationEnabled()) {
		return 0
	}
	var v = getDistance(0, 0, rect.left, rect.top)
	let c = v * 0.25
	return c * multiplyWith
}

var getTransitionDelay = function (el) {
	if (!isOpacityAnimationEnabled()) {
		return 0
	}
	let d = parseFloat(el.dataset.distance)
	let scrollY = el.closest('.lay-content').scrollTop
	let v = d - scrollY
	let c = v - window.innerHeight * 0.8
	return c * multiplyWith
}

var reduceDelay = function () {
	multiplyWith = 0
}

var makeDelayNormalAgain = function () {
	multiplyWith = 1
}

// https://javascript.plainenglish.io/javascript-algorithm-distance-between-points-7fe0026857e3
function getDistance(x1, y1, x2, y2) {
	let y = x2 - x1
	let x = y2 - y1
	return Math.sqrt(x * x + y * y)
}

var element_animate_in_on_scroll_controller_for_pageoverlays = {
	i: i,
	reduceDelay: reduceDelay,
	makeDelayNormalAgain: makeDelayNormalAgain,
}

export default element_animate_in_on_scroll_controller_for_pageoverlays
